import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';


interface Product {
  id: string;
  name: string;
  code: string;
  currentLocation?: string; // ID de l'emplacement actuel
  currentLocationData?: Location; // Données complètes de l'emplacement
  stockQuantity: number;
}

interface Location {
  id: string;
  code: string;
  name: string;
  type: 'Entrepôt' | 'Zone' | 'Rayon' | 'Frigo' | 'Congélateur';
  capacity: number;
  currentOccupancy: number;
  temperature?: string;
  products: number;
  status: 'Actif' | 'Inactif' | 'Maintenance';
}

interface ProductLocation {
  id: number;          // 👈 EXACTEMENT comme le backend
  code: string;
  nom: string;
  quantite: number;
}

export default function LocationsManagement() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Formulaires
  const [createForm, setCreateForm] = useState({ name: '', type: 'Rayon' as Location['type'], capacity: 1000 });
  const [editForm, setEditForm] = useState({ name: '', type: 'Rayon' as Location['type'], capacity: 1000, status: 'Actif' as Location['status'] });
  const [transferForm, setTransferForm] = useState({ product_id: '', from: '', to: '', quantity: 1, notes: '' });
  const [products, setProducts] = useState<Product[]>([]); // ← NOUVEAU : liste des produits
  const [currentProductLocations, setCurrentProductLocations] = useState<ProductLocation[]>([]);

  //== CHARGEMENT DES DONNÉES ===
  const fetchData = async () => {
    try {
      setLoading(true);
      const [locationsRes, productsRes] = await Promise.all([
        getResources('locations'),
        getResources('produits'), // ← NOUVEAU : charge les produits avec leur emplacement
      ]);

      setLocations(locationsRes.data.data || locationsRes.data);
      
      // Transforme les produits pour inclure l'emplacement actuel
      const productsList = (productsRes.data.data || productsRes.data).map((p: any) => ({
        id: p.id.toString(),
        name: p.name,
        code: p.sku || p.code_barre || 'N/A',
        currentLocation: p.location || '',
        currentLocationData: p.emplacement, // données complètes de l'emplacement
        stockQuantity: p.quantite || 0,
      }));
      
      setProducts(productsList);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
useEffect(() => {
  if (!transferForm.product_id) {
    setCurrentProductLocations([]);
    setTransferForm(prev => ({ ...prev, from: '' }));
    return;
  }

  const fetchProductLocations = async () => {
    try {
      const res = await getResources(`products/${transferForm.product_id}/locations`);

      // ✅ EXTRACTION SÛRE
      const locations = Array.isArray(res.data)
        ? res.data
        : res.data.emplacements || [];

      setCurrentProductLocations(locations);

      // ✅ Auto-remplissage si un seul emplacement
      if (locations.length === 1) {
        setTransferForm(prev => ({
          ...prev,
          from: locations[0].id.toString(),
        }));
      } else {
        setTransferForm(prev => ({ ...prev, from: '' }));
      }
    } catch (err) {
      toast.error('Impossible de charger les emplacements du produit');
      setCurrentProductLocations([]);
      setTransferForm(prev => ({ ...prev, from: '' }));
    }
  };

  fetchProductLocations();
}, [transferForm.product_id]);

  const filteredLocations = locations.filter(l => {
    const matchesSearch = l.code.toLowerCase().includes(searchTerm.toLowerCase()) || l.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || l.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalCapacity = locations.reduce((s, l) => s + l.capacity, 0);
  const totalOccupancy = locations.reduce((s, l) => s + l.currentOccupancy, 0);
  const activeLocations = locations.filter(l => l.status === 'Actif').length;

  const getOccupancyColor = (o: number, c: number) => {
    const p = (o / c) * 100;
    if (p >= 90) return 'text-red-600';
    if (p >= 70) return 'text-orange-600';
    return 'text-green-600';
  };

  const getOccupancyBarColor = (o: number, c: number) => {
    const p = (o / c) * 100;
    if (p >= 90) return 'bg-red-500';
    if (p >= 70) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Entrepôt': return 'ri-building-line';
      case 'Zone': return 'ri-layout-grid-line';
      case 'Rayon': return 'ri-stack-line';
      case 'Frigo': return 'ri-temp-cold-line';
      case 'Congélateur': return 'ri-snowy-line';
      default: return 'ri-map-pin-line';
    }
  };

// === AJOUT ===
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createResource('locations', {
        libelle: createForm.name,
        type_stock: createForm.type.toLowerCase().replace('é', 'e'),
        capacite: createForm.capacity,
      });
      toast.success('Emplacement créé !');
      setShowCreateModal(false);
      setCreateForm({ name: '', type: 'Rayon', capacity: 1000 });
      fetchData(); // ← recharge les deux (emplacements + produits)
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la création');
    }
  };

  // === MODIFICATION ===
  const openEdit = (loc: Location) => {
    setSelectedLocation(loc);
    setEditForm({
      name: loc.name,
      type: loc.type,
      capacity: loc.capacity,
      status: loc.status,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;

    try {
      await updateResource(`locations`, selectedLocation.id, {
        libelle: editForm.name,
        type_stock: editForm.type.toLowerCase().replace('é', 'e'),
        capacite: editForm.capacity,
        actif: editForm.status === 'Actif',
      });
      toast.success('Emplacement modifié !');
      setShowEditModal(false);
      fetchData(); // ← recharge les deux
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la modification');
    }
  };

  // === TRANSFERT ===
  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createResource('locations/stock-transfers', {
        produit_id: transferForm.product_id,
        emplacement_source_id: transferForm.from,
        emplacement_destination_id: transferForm.to,
        quantite: transferForm.quantity,
        notes: transferForm.notes,
      });
      toast.success('Transfert effectué !');
      setShowTransferModal(false);
      setTransferForm({ product_id: '', from: '', to: '', quantity: 1, notes: '' });
      fetchData(); // ← recharge pour mettre à jour les occupations
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du transfert');
    }
  };

  // === DÉTAIL ===
  const openDetail = (loc: Location) => {
    setSelectedLocation(loc);
    setShowDetailModal(true);
  };

  return (
    <>
      <Toaster position="top-right" />

      <div className="space-y-6">
        {/* 4 CARTES STATISTIQUES */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Emplacements</p>
                <p className="text-3xl font-bold mt-2">{locations.length}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-map-pin-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Emplacements Actifs</p>
                <p className="text-3xl font-bold mt-2">{activeLocations}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Capacité Totale</p>
                <p className="text-3xl font-bold mt-2">{totalCapacity}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-database-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Taux d'Occupation</p>
                <p className="text-3xl font-bold mt-2">{totalCapacity > 0 ? ((totalOccupancy / totalCapacity) * 100).toFixed(0) : 0}%</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-pie-chart-line text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* LISTE DES EMPLACEMENTS */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher par code ou nom..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
            >
              <option value="all">Tous les types</option>
              <option value="Entrepôt">Entrepôt</option>
              <option value="Zone">Zone</option>
              <option value="Rayon">Rayon</option>
              <option value="Frigo">Frigo</option>
              <option value="Congélateur">Congélateur</option>
            </select>

            <button onClick={() => setShowCreateModal(true)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-add-line"></i> Nouvel Emplacement
            </button>

            <button onClick={() => setShowTransferModal(true)} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-arrow-left-right-line"></i> Transférer
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line animate-spin text-6xl text-gray-400"></i>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLocations.map(loc => {
                const percent = (loc.currentOccupancy / loc.capacity) * 100;
                return (
                  <div key={loc.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <i className={`${getTypeIcon(loc.type)} text-2xl text-blue-600`}></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 font-mono">{loc.code}</h3>
                          <p className="text-sm text-gray-600">{loc.type}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        loc.status === 'Actif' ? 'bg-green-100 text-green-800' :
                        loc.status === 'Maintenance' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {loc.status}
                      </span>
                    </div>

                    <p className="text-gray-700 mb-4">{loc.name}</p>

                    {loc.temperature && (
                      <div className="mb-4 p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                        <i className="ri-temp-cold-line text-blue-600"></i>
                        <span className="text-sm text-blue-900 font-medium">{loc.temperature}</span>
                      </div>
                    )}

                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Occupation</span>
                          <span className={`font-bold ${getOccupancyColor(loc.currentOccupancy, loc.capacity)}`}>
                            {percent.toFixed(0)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full transition-all ${getOccupancyBarColor(loc.currentOccupancy, loc.capacity)}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{loc.currentOccupancy} / {loc.capacity} unités</p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-product-hunt-line"></i>
                          <span>{loc.products} produits</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => openDetail(loc)} className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-eye-line"></i>
                          </button>
                          <button onClick={() => openEdit(loc)} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-edit-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredLocations.length === 0 && !loading && (
            <div className="text-center py-12">
              <i className="ri-map-pin-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">Aucun emplacement trouvé</p>
            </div>
          )}
        </div>

        {/* === MODAL CRÉATION : DESIGN HARMONISÉ === */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold text-gray-900">Nouvel Emplacement</h3>
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <input 
                  required 
                  placeholder="Nom de l'emplacement" 
                  value={createForm.name} 
                  onChange={e => setCreateForm({ ...createForm, name: e.target.value })} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                />
                <select 
                  value={createForm.type} 
                  onChange={e => setCreateForm({ ...createForm, type: e.target.value as Location['type'] })} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Rayon">Rayon</option>
                  <option value="Frigo">Frigo</option>
                  <option value="Congélateur">Congélateur</option>
                  <option value="Zone">Zone</option>
                  <option value="Entrepôt">Entrepôt</option>
                </select>
                <input 
                  type="number" 
                  placeholder="Capacité (unités)" 
                  value={createForm.capacity} 
                  onChange={e => setCreateForm({ ...createForm, capacity: +e.target.value })} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                />
              </form>

              <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  onClick={handleCreate}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  Créer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === MODAL ÉDITION : DESIGN HARMONISÉ === */}
        {showEditModal && selectedLocation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold text-gray-900">Modifier {selectedLocation.code}</h3>
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)} 
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleUpdate} className="p-6 space-y-4">
                <input 
                  required 
                  value={editForm.name} 
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })} 
                  placeholder="Nom de l'emplacement" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                />
                <select 
                  value={editForm.type} 
                  onChange={e => setEditForm({ ...editForm, type: e.target.value as Location['type'] })} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Rayon">Rayon</option>
                  <option value="Frigo">Frigo</option>
                  <option value="Congélateur">Congélateur</option>
                  <option value="Zone">Zone</option>
                  <option value="Entrepôt">Entrepôt</option>
                </select>
                <input 
                  type="number" 
                  value={editForm.capacity} 
                  onChange={e => setEditForm({ ...editForm, capacity: +e.target.value })} 
                  placeholder="Capacité (unités)" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                />
                <select 
                  value={editForm.status} 
                  onChange={e => setEditForm({ ...editForm, status: e.target.value as Location['status'] })} 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Actif">Actif</option>
                  <option value="Inactif">Inactif</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </form>

              <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)} 
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  onClick={handleUpdate}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === MODAL DÉTAIL : DESIGN HARMONISÉ === */}
        {showDetailModal && selectedLocation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold text-gray-900">Détail Emplacement</h3>
                <button 
                  onClick={() => setShowDetailModal(false)} 
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Code</span>
                  <span className="font-mono font-bold text-gray-900">{selectedLocation.code}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Nom</span>
                  <span className="font-bold text-gray-900">{selectedLocation.name}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Type</span>
                  <span className="font-bold text-gray-900">{selectedLocation.type}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Capacité</span>
                  <span className="font-bold text-gray-900">{selectedLocation.capacity} unités</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Occupation</span>
                  <span className={`font-bold ${getOccupancyColor(selectedLocation.currentOccupancy, selectedLocation.capacity)}`}>
                    {selectedLocation.currentOccupancy} / {selectedLocation.capacity}
                  </span>
                </div>
                {selectedLocation.temperature && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-blue-800">Température</span>
                    <span className="font-bold text-blue-800">{selectedLocation.temperature}</span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Produits</span>
                  <span className="font-bold text-gray-900">{selectedLocation.products}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Statut</span>
                  <span className={`font-bold ${
                    selectedLocation.status === 'Actif' ? 'text-green-600' :
                    selectedLocation.status === 'Maintenance' ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    {selectedLocation.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === MODAL TRANSFERT : DYNAMIQUE + DESIGN HARMONISÉ === */}
        {showTransferModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleTransfer}>
                {/* Header identique aux autres modales */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                  <h3 className="text-2xl font-bold text-gray-900">Transfert de Stock</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTransferModal(false);
                      setTransferForm({ product_id: '', from: '', to: '', quantity: 1, notes: '' });
                      setCurrentProductLocations([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Sélection du produit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Produit <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={transferForm.product_id}
                      onChange={(e) => {
                        const productId = e.target.value;
                        setTransferForm({ ...transferForm, product_id: productId, from: '', to: '' });
                        // Le useEffect ci-dessous chargera les emplacements
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Sélectionner un produit</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Emplacement source : dynamique et pré-rempli */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emplacement source <span className="text-red-500">*</span>
                    </label>
                    {currentProductLocations.length === 0 && transferForm.product_id ? (
                      <div className="text-sm text-gray-500 italic">
                        Chargement des emplacements...
                      </div>
                    ) : currentProductLocations.length === 0 && !transferForm.product_id ? (
                      <div className="text-sm text-gray-500 italic">
                        Sélectionnez un produit pour voir ses emplacements
                      </div>
                    ) : (
                      <select
                        required
                        value={transferForm.from}
                        onChange={(e) => setTransferForm({ ...transferForm, from: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">
                          {currentProductLocations.length === 1
                            ? `${currentProductLocations[0].code} - ${currentProductLocations[0].nom} (${currentProductLocations[0].quantite} unités)`
                            : 'Choisir l\'emplacement source'}
                        </option>
                        {Array.isArray(currentProductLocations) &&
                          currentProductLocations.map(loc => (
                            <option key={loc.id} value={loc.id}>
                              {loc.code} - {loc.nom} ({loc.quantite} unités)
                            </option>
                        ))}

                      </select>
                    )}
                  </div>

                  {/* Emplacement destination */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emplacement destination <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={transferForm.to}
                      onChange={(e) => setTransferForm({ ...transferForm, to: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      disabled={transferForm.from === ''}
                    >
                      <option value="">Choisir la destination</option>
                      {locations
                        .filter(l => l.id !== transferForm.from) // Exclure la source
                        .map(l => (
                          <option key={l.id} value={l.id}>
                            {l.code} - {l.name} ({l.type})
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Quantité */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantité à transférer <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="number"
                      min="1"
                      value={transferForm.quantity}
                      onChange={(e) => setTransferForm({ ...transferForm, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Ex: 12"
                    />

                    {currentProductLocations.find(l => l.id.toString() === transferForm.from) && (
                      <p className="text-sm text-gray-600 mt-2">
                        Disponible : <strong>
                          {currentProductLocations.find(l => l.id.toString() === transferForm.from)?.quantite || 0}
                        </strong> unités
                      </p>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optionnel)</label>
                    <textarea
                      rows={3}
                      value={transferForm.notes}
                      onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                      placeholder="Motif du transfert, référence, etc."
                    />
                  </div>
                </div>

                {/* Boutons identiques aux autres modales */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTransferModal(false);
                      setTransferForm({ product_id: '', from: '', to: '', quantity: 1, notes: '' });
                      setCurrentProductLocations([]);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={!transferForm.product_id || !transferForm.from || !transferForm.to || transferForm.quantity < 1}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    Effectuer le transfert
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}